// DeepSeek API service for English sentence expansion
const DEEPSEEK_API_KEY = 'sk-7549b29d010a424a836d081a478552f4';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

// DeepSeek API pricing (per million tokens)
const PRICING = {
  input: {
    cache_hit: 0.2,    // 0.2元 per million tokens
    cache_miss: 2.0    // 2元 per million tokens
  },
  output: 3.0          // 3元 per million tokens
};

// Calculate cost based on tokens used
export const calculateCost = (inputTokens, outputTokens, cacheHit = false) => {
  const inputCost = (inputTokens / 1000000) * (cacheHit ? PRICING.input.cache_hit : PRICING.input.cache_miss);
  const outputCost = (outputTokens / 1000000) * PRICING.output;
  return inputCost + outputCost;
};

// Generate sentence expansion prompt
const generateExpansionPrompt = (keyword, language = 'english') => {
  return `Create a 15-step sentence expansion exercise for the keyword "${keyword}" in English. Please return JSON data in the following format:

{
  "topic": "${keyword}",
  "steps": [
    { "label": "Topic", "content": "${keyword}" },
    { "label": "Add Quantity", "content": "..." },
    { "label": "Add Color", "content": "..." },
    { "label": "Add Shape", "content": "..." },
    { "label": "Add Position", "content": "..." },
    { "label": "Add Verb", "content": "..." },
    { "label": "Add Manner", "content": "..." },
    { "label": "Add Description", "content": "..." },
    { "label": "Add Sound", "content": "..." },
    { "label": "Add Smell", "content": "..." },
    { "label": "Add Time", "content": "..." },
    { "label": "Add Weather", "content": "..." },
    { "label": "Add Emotion", "content": "..." },
    { "label": "Add Comparison", "content": "..." },
    { "label": "Add Imagination", "content": "..." }
  ]
}

Requirements:
1. Each step should build upon the previous step
2. Content should be vivid and interesting, suitable for elementary school learning
3. The final step should form a complete, beautiful paragraph
4. Return only JSON format, no other text explanation`;
};

// Call DeepSeek API for sentence expansion
export const generateSentenceExpansion = async (keyword, language = 'english', onProgress) => {
  const startTime = Date.now();
  let inputTokens = 0;
  let outputTokens = 0;
  
  try {
    // Update progress - starting
    if (onProgress) {
      onProgress({
        status: 'starting',
        message: 'Connecting to DeepSeek API...',
        timeElapsed: 0,
        tokensUsed: 0,
        costIncurred: 0
      });
    }

    const prompt = generateExpansionPrompt(keyword, language);
    
    // Estimate input tokens
    inputTokens = Math.ceil(prompt.length * 1.3);
    
    // Update progress - sending request
    if (onProgress) {
      onProgress({
        status: 'processing',
        message: 'Generating sentence expansion...',
        timeElapsed: Date.now() - startTime,
        tokensUsed: inputTokens,
        costIncurred: calculateCost(inputTokens, 0)
      });
    }

    const response = await fetch(DEEPSEEK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 2000
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Update progress - processing response
    if (onProgress) {
      onProgress({
        status: 'processing',
        message: 'Processing AI response...',
        timeElapsed: Date.now() - startTime,
        tokensUsed: inputTokens,
        costIncurred: calculateCost(inputTokens, 0)
      });
    }

    // Extract tokens from response
    if (data.usage) {
      inputTokens = data.usage.prompt_tokens || inputTokens;
      outputTokens = data.usage.completion_tokens || 0;
    }

    const content = data.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No content received from API');
    }

    // Parse JSON response
    let expansionData;
    try {
      // Clean the response to extract JSON
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        expansionData = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('No valid JSON found in response');
      }
    } catch (parseError) {
      console.error('Failed to parse JSON:', content);
      throw new Error('Failed to parse AI response as JSON');
    }

    const totalTime = Date.now() - startTime;
    const totalCost = calculateCost(inputTokens, outputTokens);

    // Update progress - completed
    if (onProgress) {
      onProgress({
        status: 'completed',
        message: 'Sentence expansion generated successfully!',
        timeElapsed: totalTime,
        tokensUsed: inputTokens + outputTokens,
        costIncurred: totalCost,
        data: expansionData
      });
    }

    return {
      success: true,
      data: expansionData,
      usage: {
        inputTokens,
        outputTokens,
        totalTokens: inputTokens + outputTokens
      },
      cost: totalCost,
      timeElapsed: totalTime
    };

  } catch (error) {
    const totalTime = Date.now() - startTime;
    
    // Update progress - error
    if (onProgress) {
      onProgress({
        status: 'error',
        message: `Error: ${error.message}`,
        timeElapsed: totalTime,
        tokensUsed: inputTokens + outputTokens,
        costIncurred: calculateCost(inputTokens, outputTokens)
      });
    }

    return {
      success: false,
      error: error.message,
      usage: {
        inputTokens,
        outputTokens,
        totalTokens: inputTokens + outputTokens
      },
      cost: calculateCost(inputTokens, outputTokens),
      timeElapsed: totalTime
    };
  }
};

// Format time in a readable way
export const formatTime = (milliseconds) => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
};

// Format cost in a readable way
export const formatCost = (cost) => {
  return `¥${cost.toFixed(4)}`;
};

// Generate composition compilation prompt
const generateCompositionPrompt = (compositionData) => {
  const { title, wordLength, style, nodes } = compositionData;
  
  const styleDescriptions = {
    narrative: 'narrative style with clear chronological order',
    descriptive: 'descriptive style with rich details and sensory language',
    expository: 'expository style explaining and describing',
    persuasive: 'persuasive style with arguments and persuasion',
    creative: 'creative style full of imagination'
  };

  // Build node content from already-expanded steps
  const nodesContent = nodes.map((node, index) => {
    const stepsText = node.expandedSteps.map((step, stepIndex) => 
      `${step.label}: ${step.content}`
    ).join('\n');
    return `Node ${index + 1} - "${node.name}":\n${stepsText}`;
  }).join('\n\n');

  return `You are a professional writing teacher. Please fine-tune and combine the following selected sentences into a smooth, complete ${styleDescriptions[style]} composition titled "${title}".

The composition should be approximately ${wordLength} words, and maintain a consistent ${styleDescriptions[style]} style throughout.

Here are the selected sentences organized by nodes:

${nodesContent}

Important Instructions:
1. Fine-tune each selected sentence so it naturally flows into the composition's coherence
   - Adjust sentence structure, vocabulary choices, and wording as needed
   - Ensure sentences complement each other without redundancy
   - Maintain consistency in tense, perspective, and tone

2. Create smooth, natural transitions between nodes
   - Use appropriate connecting words, phrases, or sentences
   - Ensure logical progression from one node to the next
   - Make the flow feel natural, not forced

3. Maintain consistent ${styleDescriptions[style]} style throughout
   - Follow the characteristics of ${styleDescriptions[style]}
   - Keep language suitable for elementary school students
   - Use vivid, engaging descriptions

4. Write a complete composition
   - Total length should be approximately ${wordLength} words
   - The composition should feel like a unified whole, not just connected sentences
   - Each part should meaningfully contribute to the overall narrative or theme

5. Return the composition in the following JSON format:

{
  "title": "${title}",
  "style": "${style}",
  "targetWordLength": ${wordLength},
  "nodes": ${JSON.stringify(nodes.map(node => ({
    name: node.name,
    steps: node.expandedSteps
  })))},
  "finalComposition": "The complete, fine-tuned composition that seamlessly combines all selected sentences into a cohesive ${styleDescriptions[style]} style composition. The sentences should flow naturally, with appropriate transitions, consistent style, and engaging language suitable for elementary school students."
}

6. Return ONLY valid JSON format, no other text explanations before or after
7. Ensure the finalComposition is a complete, polished composition ready for use`;
};

// Call DeepSeek API for composition generation
export const generateComposition = async (compositionData, onProgress) => {
  const startTime = Date.now();
  let inputTokens = 0;
  let outputTokens = 0;
  
  try {
    // Update progress - starting
    if (onProgress) {
      onProgress({
        status: 'starting',
        message: 'Connecting to DeepSeek API...',
        timeElapsed: 0,
        tokensUsed: 0,
        costIncurred: 0
      });
    }

    const prompt = generateCompositionPrompt(compositionData);
    
    // Estimate input tokens
    inputTokens = Math.ceil(prompt.length * 1.3);
    
    // Update progress - sending request
    if (onProgress) {
      onProgress({
        status: 'processing',
        message: 'Generating composition...',
        timeElapsed: Date.now() - startTime,
        tokensUsed: inputTokens,
        costIncurred: calculateCost(inputTokens, 0)
      });
    }

    const response = await fetch(DEEPSEEK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 4000 // Higher for compositions
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Update progress - processing response
    if (onProgress) {
      onProgress({
        status: 'processing',
        message: 'Processing AI response...',
        timeElapsed: Date.now() - startTime,
        tokensUsed: inputTokens,
        costIncurred: calculateCost(inputTokens, 0)
      });
    }

    // Extract tokens from response
    if (data.usage) {
      inputTokens = data.usage.prompt_tokens || inputTokens;
      outputTokens = data.usage.completion_tokens || 0;
    }

    const content = data.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No content received from API');
    }

    // Parse JSON response
    let compositionResult;
    try {
      // Clean the response to extract JSON - try multiple patterns
      let jsonText = content.trim();
      
      // Remove markdown code blocks if present
      jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      
      // Try to find JSON object
      const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        compositionResult = JSON.parse(jsonMatch[0]);
      } else {
        // Try to parse the entire content as JSON
        compositionResult = JSON.parse(jsonText);
      }
      
      console.log('Parsed composition result:', compositionResult);
      
      // Validate required fields
      if (!compositionResult.finalComposition && !compositionResult.final_composition && !compositionResult.composition) {
        console.warn('Warning: finalComposition field not found in response. Available fields:', Object.keys(compositionResult));
      }
    } catch (parseError) {
      console.error('Failed to parse JSON. Raw content:', content);
      console.error('Parse error:', parseError);
      throw new Error(`Failed to parse AI response as JSON: ${parseError.message}`);
    }

    const totalTime = Date.now() - startTime;
    const totalCost = calculateCost(inputTokens, outputTokens);

    // Update progress - completed
    if (onProgress) {
      onProgress({
        status: 'completed',
        message: 'Composition generated successfully!',
        timeElapsed: totalTime,
        tokensUsed: inputTokens + outputTokens,
        costIncurred: totalCost,
        data: compositionResult
      });
    }

    return {
      success: true,
      data: compositionResult,
      usage: {
        inputTokens,
        outputTokens,
        totalTokens: inputTokens + outputTokens
      },
      cost: totalCost,
      timeElapsed: totalTime
    };

  } catch (error) {
    const totalTime = Date.now() - startTime;
    
    // Update progress - error
    if (onProgress) {
      onProgress({
        status: 'error',
        message: `Error: ${error.message}`,
        timeElapsed: totalTime,
        tokensUsed: inputTokens + outputTokens,
        costIncurred: calculateCost(inputTokens, outputTokens)
      });
    }

    return {
      success: false,
      error: error.message,
      usage: {
        inputTokens,
        outputTokens,
        totalTokens: inputTokens + outputTokens
      },
      cost: calculateCost(inputTokens, outputTokens),
      timeElapsed: totalTime
    };
  }
};

// Generate refinement prompt based on level and stage
const generateRefinementPrompt = (compositionText, level, stage) => {
  const levelDescriptions = {
    primary: {
      'year1-2': {
        name: 'Primary Year 1-2',
        desc: 'Use very simple vocabulary, short sentences (5-10 words each), avoid complex sentence structures, use common adjectives and verbs, content should relate to children\'s daily life experiences'
      },
      'year3-4': {
        name: 'Primary Year 3-4',
        desc: 'Use basic vocabulary, medium-length sentences (10-15 words each), can begin using simple literary devices, content suitable for lower to middle primary school students'
      },
      'year5-6': {
        name: 'Primary Year 5-6',
        desc: 'Use richer vocabulary, sentences can be slightly longer (15-20 words each), can use literary devices like metaphors and personification, content suitable for upper primary school students'
      }
    },
    secondary: {
      'junior': {
        name: 'Junior Secondary',
        desc: 'Use more mature vocabulary, diverse sentence structures (15-25 words each), can employ various literary devices, clear logic, suitable for junior secondary school students\' reading and writing level'
      },
      'senior': {
        name: 'Senior Secondary',
        desc: 'Use rich vocabulary and idioms, complex and varied sentence structures (20-30 words each), employ various literary devices, clear and profound arguments, suitable for senior secondary school students\' reading and writing level'
      }
    },
    university: {
      'general': {
        name: 'University Level',
        desc: 'Use precise advanced vocabulary and academic terminology, complex and diverse sentence structures (25-40 words each), employ various literary devices and writing techniques, rigorous and profound arguments, suitable for university students'
      }
    },
    adult: {
      'general': {
        name: 'Adult Level',
        desc: 'Use refined and elegant vocabulary, flexible and varied sentence structures (20-35 words each), employ advanced literary devices and writing techniques, precise and profound expression, suitable for adult readers'
      }
    }
  };

  const stageInfo = levelDescriptions[level]?.[stage] || levelDescriptions.primary['year5-6'];
  
  return `You are a professional writing teacher. Please refine and adjust the following composition to ${stageInfo.name} writing level.

Original Composition:
${compositionText}

Requirements:
1. ${stageInfo.desc}

2. Keep the original composition's theme and core content unchanged, only adjust vocabulary, sentence structure, and expression style

3. Ensure logical coherence of the composition, with natural and smooth transitions between paragraphs

4. Maintain the completeness of the composition, do not omit important content

5. Return only the refined composition text, do not add any other explanatory text

Please directly output the complete refined composition:`;
};

// Refine composition to specific level and stage
export const refineComposition = async (compositionText, level, stage, onProgress) => {
  const startTime = Date.now();
  let inputTokens = 0;
  let outputTokens = 0;
  
  try {
    // Update progress - starting
    if (onProgress) {
      onProgress({
        status: 'starting',
        message: 'Connecting to DeepSeek API...',
        timeElapsed: 0,
        tokensUsed: 0,
        costIncurred: 0
      });
    }

    const prompt = generateRefinementPrompt(compositionText, level, stage);
    
    // Estimate input tokens
    inputTokens = Math.ceil(prompt.length * 1.3);
    
    // Update progress - sending request
    if (onProgress) {
      onProgress({
        status: 'processing',
        message: 'Refining composition...',
        timeElapsed: Date.now() - startTime,
        tokensUsed: inputTokens,
        costIncurred: calculateCost(inputTokens, 0)
      });
    }

    const response = await fetch(DEEPSEEK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 4000
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Update progress - processing response
    if (onProgress) {
      onProgress({
        status: 'processing',
        message: 'Processing AI response...',
        timeElapsed: Date.now() - startTime,
        tokensUsed: inputTokens,
        costIncurred: calculateCost(inputTokens, 0)
      });
    }

    // Extract tokens from response
    if (data.usage) {
      inputTokens = data.usage.prompt_tokens || inputTokens;
      outputTokens = data.usage.completion_tokens || 0;
    }

    const content = data.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No content received from API');
    }

    // Clean the refined text
    let refinedText = content.trim();
    // Remove any markdown formatting or extra text
    refinedText = refinedText.replace(/^```[\w]*\n?/g, '').replace(/```$/g, '');
    refinedText = refinedText.trim();

    const totalTime = Date.now() - startTime;
    const totalCost = calculateCost(inputTokens, outputTokens);

    // Update progress - completed
    if (onProgress) {
      onProgress({
        status: 'completed',
        message: 'Composition refined successfully!',
        timeElapsed: totalTime,
        tokensUsed: inputTokens + outputTokens,
        costIncurred: totalCost,
        data: refinedText
      });
    }

    return {
      success: true,
      refinedText: refinedText,
      usage: {
        inputTokens,
        outputTokens,
        totalTokens: inputTokens + outputTokens
      },
      cost: totalCost,
      timeElapsed: totalTime
    };

  } catch (error) {
    const totalTime = Date.now() - startTime;
    
    // Update progress - error
    if (onProgress) {
      onProgress({
        status: 'error',
        message: `Error: ${error.message}`,
        timeElapsed: totalTime,
        tokensUsed: inputTokens + outputTokens,
        costIncurred: calculateCost(inputTokens, outputTokens)
      });
    }

    return {
      success: false,
      error: error.message,
      usage: {
        inputTokens,
        outputTokens,
        totalTokens: inputTokens + outputTokens
      },
      cost: calculateCost(inputTokens, outputTokens),
      timeElapsed: totalTime
    };
  }
};
